import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, Output, EventEmitter, Inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ToastrService } from 'ngx-toastr';
import { AccommodationService } from '../../../features/accommodation/services/accommodation.service';
import { EmployeeService } from '../../../features/employees/employee.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { dateRangeValidator } from '../../../helpers/helper';

@Component({
  selector: 'allocate-employee-dialog',
  templateUrl: './allocate-employee.component.html',
  styleUrls: ['../accommodation.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatOptionModule,
    CommonModule,
    MatDatepickerModule,
    NgxMatSelectSearchModule,
  ],
})
export class AllocateEmployeeDialog implements OnInit {
  @Output() employeeAllocated = new EventEmitter<any>();
  allocateForm!: FormGroup;
  propertyData: any;
  employeesAllocationData: any;
  loading: boolean = false;
  today: Date = new Date();
  employeesData: any[] = [];
  minEndDate: Date | null = null;
  private employeeSearchSubject = new Subject<string>();
  releaseEmployee: boolean = false;

  constructor(
    private allocateEmployeeService: AccommodationService,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<AllocateEmployeeDialog>,
    private http: HttpClient,
    private employeeService: EmployeeService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.employeesAllocationData = this.data?.employeeData;
      this.propertyData = this.data.propertyData;
      this.releaseEmployee = this.data.releaseEmployee;
    }
    this.initForm();
    this.getAllEmployees();
    this.employeeSearchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllEmployees(searchTerm);
      });
  }

  initForm(): void {
    this.allocateForm = this.fb.group(
      {
        employeeId: [
          this.employeesAllocationData?.employeeId || null,
          [Validators.required],
        ],
        startDate: [
          this.employeesAllocationData?.startDate || null,
          [Validators.required],
        ],
        endDate: [this.employeesAllocationData?.endDate || null],
      },
    );
  
    if (this.employeesAllocationData?.startDate) {
      this.minEndDate = this.employeesAllocationData.startDate;
    }

    if (this.releaseEmployee) {
      this.allocateForm.get('employeeId')?.disable();
      this.allocateForm.get('startDate')?.disable();
    }
  }
  

  onSearchEmployee(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.employeeSearchSubject.next(searchTerm);
  }

  getAllEmployees(search: string = '') {
    this.employeeService.getEmployeeApprovers(search).subscribe({
      next: (res) => {
        this.employeesData = res;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  
  onEndDateChange(event: any): void {
    const startDate = this.allocateForm.get('startDate')?.value;
    if (!startDate) {
      this.toastr.error('Please select Start Date first');
      this.allocateForm.get('endDate')?.reset();
      return;
    }
    this.allocateForm.updateValueAndValidity();

    const endDate = event.value;
    if (endDate < startDate) {
      this.toastr.error('End Date cannot be before Start Date');
      this.allocateForm.get('endDate')?.reset();
    }
  }

  onStartDateChange(event: any): void {
    this.allocateForm.updateValueAndValidity();
    this.minEndDate = event.value;
  }

  onAllocateEmployee(): void {
    if (this.allocateForm.invalid) {
      this.allocateForm.markAllAsTouched();
      return;
    }
    const formValue = this.allocateForm.value;
    if (
      formValue.startDate &&
      formValue.endDate &&
      formValue.startDate > formValue.endDate
    ) {
      this.toastr.error('End date must be after Start date.');
      return;
    }

    const createEmployeeWithPropertyInput = {
      propertyId: Number(this.propertyData?.id) || Number(this.propertyData?.propertyId),
      employeeId: Number(formValue.employeeId),
      startDate: formValue.startDate,
      endDate: formValue.endDate,
    };

    const updateEmployeeWithPropertyInput = {
      id: Number(this.employeesAllocationData?.allocationId),
      propertyId: Number(this.propertyData?.id),
      employeeId: Number(formValue.employeeId),
      startDate: formValue.startDate,
      endDate: formValue.endDate,
    };

    if (this.employeesAllocationData) {
      this.allocateEmployeeService
        .updateAllocatedEmployeee(updateEmployeeWithPropertyInput)
        .subscribe({
          next: (res) => {
            console.log(res);
            this.toastr.success('Employee allocation updated');
            this.dialogRef.close('success');
          },
          error: (err) => {
            console.log(err);
            this.toastr.error(err?.message);
          },
        });
    } else {
      this.allocateEmployeeService
        .allocateEmployee(createEmployeeWithPropertyInput)
        .subscribe({
          next: (res) => {
            console.log(res);
            this.toastr.success('Employee allocation successfull');
            this.dialogRef.close('success');
          },
          error: (err) => {
            console.log(err);
            this.toastr.error(err?.message);
          },
        });
    }
  }
}
